import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Transaction } from '@/types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FileText, Calendar, DollarSign, Building, User } from 'lucide-react';

interface BillTemplateProps {
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
    documentNumber?: string;
    address?: {
      street?: string;
      number?: string;
      complement?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  };
  bankDetails?: {
    bank?: string;
    agency?: string;
    account?: string;
    pixKey?: string;
  };
  billNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
  companyDocument?: string;
  companyAddress?: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  className?: string;
}

export function BillTemplate({
  transaction,
  client,
  bankDetails,
  billNumber,
  issueDate,
  companyName,
  companyLogo,
  companyDocument,
  companyAddress,
  className
}: BillTemplateProps) {
  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Format dates
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    return format(new Date(date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  // Calculate days until due
  const getDaysUntilDue = () => {
    if (!transaction.due_date) return null;
    const due = new Date(transaction.due_date);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = getDaysUntilDue();

  return (
    <Card className={`max-w-4xl mx-auto print:shadow-none print:border-none bg-card ${className}`}>
      <CardContent className="p-8">
        {/* Header */}
        <div className="text-center mb-8">
          {companyLogo ? (
            <img
              src={companyLogo}
              alt={companyName}
              className="h-16 mx-auto mb-4 object-contain max-w-xs"
              style={{ maxHeight: '64px' }}
            />
          ) : (
            <div className="text-2xl font-bold mb-4 text-terracotta-700">
              {companyName}
            </div>
          )}
          <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
            <FileText className="h-8 w-8" />
            COBRANÇA
          </h1>
          <p className="text-muted-foreground">Nº {billNumber}</p>
          <p className="text-sm text-muted-foreground">
            Emitido em: {formatDate(issueDate)}
          </p>
          {companyDocument && (
            <p className="text-sm text-muted-foreground">
              CNPJ: {companyDocument}
            </p>
          )}
        </div>

        <Separator className="mb-6" />

        {/* Transaction Info */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="h-5 w-5 text-terracotta-600" />
            <Badge variant="default" className="bg-terracotta-100 text-terracotta-800">
              {transaction.is_paid ? 'PAGO' : 'A PAGAR'}
            </Badge>
            {daysUntilDue !== null && (
              <Badge
                variant={daysUntilDue < 0 ? "destructive" : daysUntilDue <= 7 ? "secondary" : "outline"}
              >
                {daysUntilDue < 0
                  ? `${Math.abs(daysUntilDue)} dias de atraso`
                  : daysUntilDue === 0
                  ? 'Vence hoje'
                  : `Vence em ${daysUntilDue} dias`
                }
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Descrição</p>
              <p className="font-medium">{transaction.description}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Valor da Cobrança</p>
              <p className="text-2xl font-bold text-terracotta-600">
                {formatCurrency(transaction.amount)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Data de Vencimento</p>
              <p className="font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {transaction.due_date ? formatDate(transaction.due_date) : 'Não definido'}
              </p>
            </div>
            {transaction.category && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Categoria</p>
                <Badge variant="secondary">{transaction.category}</Badge>
              </div>
            )}
          </div>
        </div>

        <Separator className="mb-6" />

        {/* Client Info */}
        {client && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <User className="h-5 w-5" />
              Dados do Cliente
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Nome</p>
                <p className="font-medium">{client.name}</p>
              </div>
              {client.documentNumber && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">CPF/CNPJ</p>
                  <p className="font-medium">{client.documentNumber}</p>
                </div>
              )}
              {client.email && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">E-mail</p>
                  <p className="font-medium">{client.email}</p>
                </div>
              )}
              {client.phone && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Telefone</p>
                  <p className="font-medium">{client.phone}</p>
                </div>
              )}
            </div>
            {client.address && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-1">Endereço</p>
                <p className="font-medium">
                  {[
                    client.address.street,
                    client.address.number,
                    client.address.complement
                  ].filter(Boolean).join(', ')}
                </p>
                {client.address.neighborhood && (
                  <p className="font-medium">
                    {client.address.neighborhood}
                  </p>
                )}
                <p className="font-medium">
                  {[
                    client.address.city,
                    client.address.state,
                    client.address.zipCode
                  ].filter(Boolean).join(' - ')}
                </p>
              </div>
            )}
          </div>
        )}

        <Separator className="mb-6" />

        {/* Payment Info */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Building className="h-5 w-5" />
            Dados para Pagamento
          </h3>
          {bankDetails ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bankDetails.bank && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Banco</p>
                  <p className="font-medium">{bankDetails.bank}</p>
                </div>
              )}
              {bankDetails.agency && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Agência</p>
                  <p className="font-medium">{bankDetails.agency}</p>
                </div>
              )}
              {bankDetails.account && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Conta Corrente</p>
                  <p className="font-medium">{bankDetails.account}</p>
                </div>
              )}
              {bankDetails.pixKey && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Chave PIX</p>
                  <p className="font-medium">{bankDetails.pixKey}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground italic">
              Os dados para pagamento não foram informados. Entre em contato para obter as informações necessárias.
            </p>
          )}
        </div>

        {/* Additional Info */}
        {transaction.notes && (
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-1">Observações</p>
            <p className="text-sm">{transaction.notes}</p>
          </div>
        )}

        <Separator className="mb-6" />

        {/* Company Address */}
        {companyAddress && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3">Endereço da Empresa</h3>
            <p className="text-sm text-muted-foreground">
              {[
                companyAddress.street,
                companyAddress.number,
                companyAddress.complement
              ].filter(Boolean).join(', ')}
            </p>
            <p className="text-sm text-muted-foreground">
              {[
                companyAddress.neighborhood,
                companyAddress.city,
                companyAddress.state,
                companyAddress.zipCode
              ].filter(Boolean).join(' - ')}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>Esta cobrança deverá ser paga até a data de vencimento para evitar multas e juros.</p>
          <p className="mt-2">
            {companyName} - {formatDate(issueDate)}
          </p>
          {companyDocument && (
            <p className="mt-1">
              CNPJ: {companyDocument}
            </p>
          )}
        </div>

        {/* Terms and Conditions */}
        <div className="mt-8 pt-4 border-t">
          <h4 className="font-semibold mb-2 text-sm">Termos e Condições</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• O pagamento deve ser realizado até a data de vencimento indicada.</li>
            <li>• Após o vencimento, serão aplicados juros de mora de 1% ao mês e multa de 2%.</li>
            <li>• Esta cobrança pode ser paga pelos métodos indicados acima.</li>
            <li>• Em caso de dúvidas, entre em contato conosco.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}